import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

/**
 * MasterDataContext — app-wide cache for reference data (locations,
 * postings, departments, geography-types, etc.) that:
 *
 *   - rarely changes between user actions
 *   - is read by many forms / modals across the app
 *   - cost a backend round-trip to Mumbai PG every time
 *
 * Design:
 *   - **Lazy fetch:** components call `fetchX()` only when they actually
 *     need the data (e.g. when a modal opens). The list page itself
 *     never fetches ref data — only its own records.
 *   - **TTL:** each entry stores `fetchedAt`. If `(now - fetchedAt) < ttl`,
 *     the cached value is returned synchronously without a network call.
 *     If stale, a fresh GET is fired, and the resolved value replaces
 *     the cache. While fetching, multiple concurrent callers share the
 *     same in-flight promise (request dedupe).
 *   - **Event invalidation:** mutating services (create / update / delete
 *     a location, etc.) call `invalidateX()` to mark the cache stale
 *     immediately. Next read fetches fresh.
 *   - **Cross-user freshness:** TTL is the safety net. If User A creates
 *     a location, User B's cache expires within `ttl` ms and B's next
 *     read sees the new data automatically. No WebSocket needed.
 *
 * Adding a new ref-data type:
 *   1. Add it to the `cache` initial shape below
 *   2. Add a fetcher entry to `FETCHERS`
 *   3. Set a TTL in `TTLS`
 *   4. Export a typed `useXxx()` convenience hook if desired
 *
 * Cross-tab / cross-user invalidation is OUT OF SCOPE here. If business
 * demands it for a specific data type, lift that type to a backend
 * cache (Redis) with a pub-sub broadcast — but only when warranted.
 */

import organizationLocationsService from '../services/organizationLocationsService';
import postingService from '../services/postingService';
import { geographyTypeService } from '../services/geographyTypeService';

// ---------- Types ----------

type CacheKey = 'locations' | 'postings' | 'geographyTypes';

interface CacheEntry<T> {
  data: T | null;
  fetchedAt: number; // ms since epoch
  inFlight: Promise<T> | null;
}

type CacheState = {
  [K in CacheKey]: CacheEntry<any>;
};

// ---------- Per-type TTLs (ms) ----------
//
// Rule of thumb: "what's the worst that happens if a user sees this 60s
// out of date?" If nothing bad — go higher. If business-critical — go
// lower (or skip caching entirely).
const TTLS: Record<CacheKey, number> = {
  locations: 10 * 60 * 1000,      // 10 min — offices rarely added
  postings: 10 * 60 * 1000,       // 10 min — job roles rarely change
  geographyTypes: 60 * 60 * 1000, // 1 hour — effectively an enum
};

// ---------- Fetchers ----------
// Each fetcher normalises its service's response shape into a plain array
// so consumers don't have to peek into `.data` / pagination wrappers.

const FETCHERS: Record<CacheKey, () => Promise<any>> = {
  locations: async () => {
    // Approved + active only — the /dropdown endpoint filters server-side, so
    // pickers backed by this cache don't leak draft/unapproved locations.
    return await organizationLocationsService.getDropdown();
  },
  postings: async () => {
    const res = await postingService.getAll({ limit: 100 });
    return res?.data ?? [];
  },
  geographyTypes: async () => {
    // Approved + active only — /dropdown filters workflow_state='approved'
    // server-side, so pickers backed by this cache don't leak draft geo types.
    return await geographyTypeService.dropdown();
  },
};

// ---------- Context shape ----------

interface MasterDataContextType {
  // Generic accessor used by the convenience hooks below.
  get: <T = any>(key: CacheKey) => T | null;
  // Trigger a fetch if cache is stale / empty. Returns the resolved data.
  // Multiple concurrent callers share one in-flight promise.
  fetch: <T = any>(key: CacheKey, opts?: { force?: boolean }) => Promise<T>;
  // Mark an entry stale. Next `fetch()` will hit the network.
  invalidate: (key: CacheKey) => void;
  // For diagnostics / dev tools.
  isStale: (key: CacheKey) => boolean;
}

const MasterDataContext = createContext<MasterDataContextType | undefined>(undefined);

// ---------- Provider ----------

const initialEntry = <T,>(): CacheEntry<T> => ({
  data: null,
  fetchedAt: 0,
  inFlight: null,
});

const initialCache: CacheState = {
  locations: initialEntry(),
  postings: initialEntry(),
  geographyTypes: initialEntry(),
};

export const MasterDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Cache itself lives in a ref (no re-renders on every fetch). We expose
  // a separate `version` state that bumps when data actually changes, so
  // subscribers via the convenience hooks (below) can re-render.
  const cacheRef = useRef<CacheState>(initialCache);
  const [, setVersion] = useState(0);
  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  const isStale = useCallback((key: CacheKey): boolean => {
    const entry = cacheRef.current[key];
    if (!entry.data) return true;
    return Date.now() - entry.fetchedAt > TTLS[key];
  }, []);

  const get = useCallback(<T,>(key: CacheKey): T | null => {
    return cacheRef.current[key].data as T | null;
  }, []);

  const fetchEntry = useCallback(
    async <T,>(key: CacheKey, opts?: { force?: boolean }): Promise<T> => {
      const entry = cacheRef.current[key];
      const fresh = entry.data && !isStale(key) && !opts?.force;
      if (fresh) return entry.data as T;
      if (entry.inFlight) return entry.inFlight as Promise<T>;

      const promise = (async () => {
        try {
          const data = await FETCHERS[key]();
          cacheRef.current[key] = {
            data,
            fetchedAt: Date.now(),
            inFlight: null,
          };
          bumpVersion();
          return data as T;
        } catch (err) {
          // Clear in-flight on failure so the next call can retry.
          cacheRef.current[key] = { ...cacheRef.current[key], inFlight: null };
          throw err;
        }
      })();

      cacheRef.current[key] = { ...entry, inFlight: promise };
      return promise;
    },
    [bumpVersion, isStale],
  );

  const invalidate = useCallback(
    (key: CacheKey) => {
      // Wipe data + timestamp. Don't auto-fetch — next consumer will.
      cacheRef.current[key] = initialEntry();
      bumpVersion();
    },
    [bumpVersion],
  );

  const value = useMemo<MasterDataContextType>(
    () => ({ get, fetch: fetchEntry, invalidate, isStale }),
    [get, fetchEntry, invalidate, isStale],
  );

  return <MasterDataContext.Provider value={value}>{children}</MasterDataContext.Provider>;
};

// ---------- Hooks ----------

export function useMasterData(): MasterDataContextType {
  const ctx = useContext(MasterDataContext);
  if (!ctx) throw new Error('useMasterData must be used inside <MasterDataProvider>');
  return ctx;
}

/**
 * useCachedResource — convenience hook for one ref-data type.
 *
 *   const { data, loading, refresh } = useCachedResource('locations', { enabled: isModalOpen });
 *
 * - `enabled` defaults to true; pass `isModalOpen` to make it truly lazy.
 * - On enable, triggers a fetch (no-op if cache fresh).
 * - `refresh()` forces a re-fetch (e.g. after the user creates a record).
 */
export function useCachedResource<T = any>(
  key: CacheKey,
  opts: { enabled?: boolean } = {},
): { data: T[] | null; loading: boolean; refresh: () => Promise<void> } {
  const { get, fetch } = useMasterData();
  const enabled = opts.enabled !== false;
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetch<T[]>(key)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, fetch]);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      await fetch<T[]>(key, { force: true });
    } finally {
      setLoading(false);
    }
  }, [fetch, key]);

  return { data: get<T[]>(key), loading, refresh };
}
